import React from 'react';
import { UseFormRegister, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { BankDetails, BankingField } from '../../utils/types';
import { Upload, X as CloseIcon, FileText, ExternalLink } from 'lucide-react';
import {
  preventInvalidNameInput,
  VALIDATION_MESSAGES,
} from '../../utils/validationMessages';
import { IMAGE_API_BASE_URL } from '../../config/api';
import SourceCombobox from '../common/SourceCombobox';
import DynamicBankingFields from './DynamicBankingFields';

export interface CountryOption {
  name: string;
  currency: string;
  isoCode?: string;
  banking?: {
    isoCode?: string;
    fields?: BankingField[];
  };
}

export const DOCUMENT_TYPES = [
  { value: 'BANK_CERTIFICATE', label: 'Bank Account Certificate' },
  { value: 'BANK_LETTER', label: 'Bank Letter' },
  { value: 'BANK_STATEMENT', label: 'Bank Statement' },
  { value: 'CANCELLED_CHEQUE', label: 'Cancelled Cheque' },
].sort((a, b) => a.label.localeCompare(b.label));

interface BankFormFieldsProps {
  register: UseFormRegister<BankDetails>;
  setValue: UseFormSetValue<BankDetails>;
  errors: FieldErrors<BankDetails>;
  selectedCountry: string;
  countryIsoCode?: string;
  selectedDocType: string;
  countriesList: CountryOption[];
  isCountriesLoading: boolean;
  bankFile: File | null;
  bankFilePreview: string | null;
  onCountryChange: (value: string) => void;
  onDocTypeChange?: (value: string) => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearFile: () => void;
}

const ErrorMsg = ({ message }: { message?: string }) =>
  message ? (
    <p className="text-xs text-red-500 font-medium mt-1">{message}</p>
  ) : null;

const BankFormFields: React.FC<BankFormFieldsProps> = ({
  register,
  setValue,
  errors,
  selectedCountry,
  selectedDocType,
  countriesList,
  isCountriesLoading,
  bankFile,
  bankFilePreview,
  onCountryChange,
  onDocTypeChange,
  onFileUpload,
  onClearFile,
}) => {
  const selectedCountryData = countriesList.find(
    c => c.name === selectedCountry
  );
  const dynamicFields = selectedCountryData?.banking?.fields || [];

  const selectedDocLabel =
    DOCUMENT_TYPES.find(d => d.value === selectedDocType)?.label ||
    'Bank Verification Document';

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-muted/20 p-4 rounded-xl border border-border/60">
        <div className="space-y-2">
          <Label
            htmlFor="bankCountry"
            className="flex items-center gap-1.5 text-sm font-semibold"
          >
            Bank Country *
          </Label>
          <SourceCombobox
            id="bankCountry"
            items={countriesList.map(c => c.name)}
            placeholder={
              isCountriesLoading
                ? 'Loading countries...'
                : 'Select Bank Country'
            }
            value={selectedCountry}
            onChange={onCountryChange}
            disabled={isCountriesLoading}
          />
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="currency"
            className="flex items-center gap-1.5 text-sm font-semibold"
          >
            Account Currency
          </Label>
          <Input
            id="currency"
            {...register('currency')}
            className="bg-muted/40 font-mono  cursor-not-allowed"
            disabled={true}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="accountName" className="text-sm font-semibold">
            Account Holder Name *
          </Label>
          <Input
            id="accountName"
            onKeyDown={preventInvalidNameInput}
            {...register('accountName', {
              onChange: e => {
                setValue('accountName', e.target.value, {
                  shouldValidate: true,
                });
              },
              required: VALIDATION_MESSAGES.accountName.required,
              minLength: {
                value: 2,
                message: VALIDATION_MESSAGES.fullName.minLength,
              },
              maxLength: {
                value: 50,
                message: VALIDATION_MESSAGES.fullName.maxLength,
              },
              pattern: {
                value: /^[a-zA-Z \-']+$/,
                message: VALIDATION_MESSAGES.fullName.pattern,
              },
            })}
            className={
              errors.accountName
                ? 'border-red-500 focus-visible:ring-red-500'
                : ''
            }
          />
          <ErrorMsg message={errors.accountName?.message} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="bankName" className="text-sm font-semibold">
            Bank Name *
          </Label>
          <Input
            id="bankName"
            onKeyDown={preventInvalidNameInput}
            {...register('bankName', {
              onChange: e => {
                setValue('bankName', e.target.value, { shouldValidate: true });
              },
              required: VALIDATION_MESSAGES.bankName.required,
              minLength: {
                value: 3,
                message: 'Bank name must be at least 3 characters',
              },
              maxLength: {
                value: 50,
                message: 'Bank name cannot exceed 50 characters',
              },
            })}
            className={
              errors.bankName ? 'border-red-500 focus-visible:ring-red-500' : ''
            }
          />
          <ErrorMsg message={errors.bankName?.message} />
        </div>
      </div>

      <DynamicBankingFields
        fields={dynamicFields}
        register={register}
        setValue={setValue}
        errors={errors}
      />

      <div className="space-y-4 pt-2 border-t border-border/80">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label
              htmlFor="verificationDocumentType"
              className="text-sm font-semibold"
            >
              Select Document Type *
            </Label>
            <SourceCombobox
              id="verificationDocumentType"
              items={DOCUMENT_TYPES.map(doc => ({
                value: doc.value,
                label: doc.label,
              }))}
              placeholder="Select document type"
              value={selectedDocType}
              onChange={value => {
                if (onDocTypeChange) {
                  onDocTypeChange(value);
                } else {
                  setValue('verificationDocumentType', value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
              }}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label
            htmlFor="verificationDocument"
            className="text-sm font-semibold block"
          >
            Upload {selectedDocLabel} *
          </Label>
          <div className="border-2 border-dashed border-border rounded-xl p-6 bg-muted/5 hover:bg-muted/10 transition-colors flex items-center justify-center">
            <Input
              id="verificationDocument"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf,.pdf"
              className="hidden"
              onChange={onFileUpload}
            />
            <label
              htmlFor="verificationDocument"
              className="cursor-pointer w-full"
            >
              <div className="flex flex-col items-center space-y-3">
                {bankFilePreview ? (
                  <div className="relative group">
                    {(() => {
                      const rawUrl = (bankFilePreview || '').trim();
                      const isDirectUrl =
                        rawUrl.startsWith('blob:') ||
                        rawUrl.startsWith('data:') ||
                        rawUrl.startsWith('http://') ||
                        rawUrl.startsWith('https://');

                      const baseUrl = (IMAGE_API_BASE_URL || '')
                        .trim()
                        .replace(/\/+$/, '');
                      const fullUrl = isDirectUrl
                        ? rawUrl
                        : `${baseUrl}${rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`}`;

                      const isPdf =
                        bankFile?.type === 'application/pdf' ||
                        bankFile?.name.toLowerCase().endsWith('.pdf') ||
                        (typeof rawUrl === 'string' &&
                          !bankFile &&
                          (rawUrl.toLowerCase().includes('.pdf') ||
                            rawUrl.startsWith('data:application/pdf')));

                      if (isPdf) {
                        return (
                          <a
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Click to view PDF document"
                            className="flex flex-col items-center justify-center w-32 h-32 md:w-44 md:h-44 p-3 rounded-lg border-2 border-primary/20 bg-muted/20 hover:bg-muted/30 shadow-md transition-transform group-hover:scale-[1.02] text-center"
                            onClick={e => e.stopPropagation()}
                          >
                            <FileText className="h-10 w-10 text-red-500 mb-2" />
                            <span className="text-xs font-semibold line-clamp-2 break-all text-foreground">
                              {bankFile ? bankFile.name : 'PDF Document'}
                            </span>
                            <span className="text-[10px] text-primary hover:underline mt-1 font-medium flex items-center gap-0.5">
                              View PDF <ExternalLink className="h-2.5 w-2.5" />
                            </span>
                          </a>
                        );
                      }

                      return (
                        <a
                          href={fullUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Click to view full image"
                          className="block relative group cursor-pointer"
                          onClick={e => e.stopPropagation()}
                        >
                          <img
                            src={fullUrl}
                            alt="Document Preview"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            className="w-32 h-32 md:w-44 md:h-44 object-contain rounded-lg border-2 border-primary/20 shadow-md transition-transform group-hover:scale-[1.02] bg-white dark:bg-zinc-900"
                          />
                        </a>
                      );
                    })()}

                    <Button
                      type="button"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClearFile();
                      }}
                      className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full p-1.5 shadow-lg border-2 border-white hover:bg-red-600 transition-colors z-10"
                    >
                      <CloseIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="p-3 bg-primary/10 rounded-full">
                      <Upload className="h-8 w-8 text-primary" />
                    </div>
                    <div className="text-center space-y-1">
                      <span className="text-sm font-semibold text-foreground block">
                        {bankFile
                          ? bankFile.name
                          : `Click to upload ${selectedDocLabel}`}
                      </span>
                      <span className="text-xs text-muted-foreground block">
                        PNG, JPG, JPEG, WEBP, PDF (MAX. 10MB)
                      </span>
                    </div>
                  </>
                )}
              </div>
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

export default BankFormFields;
